import {useState} from 'react';
import Popup from "../../../components/general/Popup.tsx";
import FormDropdown from "../../../components/general/FormDropdown.tsx";
import Checkbox from "../../../components/general/Checkbox.tsx";
import {DefaultVatRateOption} from "../types/VatCode.ts";
import {BookingProvider, isDefaultBookingProvider} from '../../../types/enums.ts';
import type { GlAccount } from "../../../hooks/useGLAccounts.ts";
import { GLASelector, GlaFilterPreset } from "../../../components/specific/GLASelector";
import type {DocumentDirection} from "../../../generated/enums.ts";

type BulkActionsPopupProps = {
    open: boolean;
    onClose: () => void;
    onApply: (
        vatId: string | null,
        defaultBookCategoryId: string | null,
        flipSign: boolean,
        fromTotalToNet: boolean,
        mergeAllLineItems: boolean
    ) => void;
    glAccounts: GlAccount[];
    documentDirection: DocumentDirection;
    bookingProvider: BookingProvider;
    defaultVatRateOptions: DefaultVatRateOption[];
}

export function BulkActionsPopup({
    open,
    onClose,
    onApply,
    glAccounts,
    documentDirection,
    bookingProvider,
    defaultVatRateOptions
}: BulkActionsPopupProps) {
    const hasDefaultBooking = isDefaultBookingProvider(bookingProvider);
    const canBookDocument = hasDefaultBooking;

    const [selectedVatId, setSelectedVatId] = useState<string | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<GlAccount | null>(null);
    const [flipSign, setFlipSign] = useState(false);
    const [fromTotalToNet, setFromTotalToNet] = useState(false);
    const [mergeAllLineItems, setMergeAllLineItems] = useState(false);
    const areOtherActionsDisabled = mergeAllLineItems;
    const hasBulkVatSelected = Boolean(selectedVatId);
    const hasBulkGlaSelected = canBookDocument && Boolean(selectedCategory);

    const isMergeLineItemsBlocked = hasBulkVatSelected || hasBulkGlaSelected || flipSign || fromTotalToNet;

    const handleClose = () => {
        setSelectedVatId(null);
        setSelectedCategory(null);
        setFlipSign(false);
        setFromTotalToNet(false);
        setMergeAllLineItems(false);
        onClose();
    };

    const handleApply = () => {
        onApply(
            selectedVatId,
            selectedCategory?.id ?? null,
            flipSign,
            fromTotalToNet,
            mergeAllLineItems
        );
        handleClose();
    };

    const handleMergeAllLineItemsChange = (isChecked: boolean) => {
        setMergeAllLineItems(isChecked);
        if (isChecked) {
            setFlipSign(false);
            setFromTotalToNet(false);
        }
    };

    return (
        <Popup
            open={open}
            onClose={handleClose}
            onConfirm={handleApply}
            title="Bulk acties"
            confirmButtonDisplayLabel="Toepassen"
            Icon={null}
        >
            <div className="space-y-4">
                {canBookDocument && (
                    <div className="w-full [&_*]:!max-w-none">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Grootboekrekening
                        </label>
                        {hasDefaultBooking && (
                            <GLASelector
                                accounts={glAccounts}
                                filterPreset={GlaFilterPreset.PROFIT_AND_LOSS}
                                documentDirectionFilter={documentDirection}
                                value={selectedCategory}
                                onChange={setSelectedCategory}
                                placeholder="Selecteer grootboekrekening"
                                disabled={areOtherActionsDisabled}
                            />
                        )}
                    </div>
                )}
                <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        BTW code
                    </label>
                    <FormDropdown
                        value={selectedVatId ?? ''}
                        onChange={(value) => setSelectedVatId(value ? String(value) : null)}
                        options={defaultVatRateOptions}
                        showClearButton={true}
                        disabled={areOtherActionsDisabled}
                    />
                </div>
                <div className="pt-4 border-t border-gray-100 space-y-4">
                    <div className={`flex flex-col gap-1 ${areOtherActionsDisabled ? 'opacity-50' : ''}`}>
                        <Checkbox
                            value={flipSign}
                            onUpdateFunc={setFlipSign}
                            label="Teken omdraaien"
                        />
                        <span className="text-xs text-gray-500 ml-6">
                            Vermenigvuldigt alle regels met -1
                        </span>
                    </div>
                    <div className={`flex flex-col gap-1 ${areOtherActionsDisabled ? 'opacity-50' : ''}`}>
                        <Checkbox
                            value={fromTotalToNet}
                            onUpdateFunc={setFromTotalToNet}
                            label="Van totaal naar netto"
                        />
                        <span className="text-xs text-gray-500 ml-6">
                            Verlaagt het netto bedrag op basis van het BTW percentage (als het totaalbedrag als netto is ingevuld)
                        </span>
                    </div>
                    <div className="flex flex-col gap-1">
                        <Checkbox
                            value={mergeAllLineItems}
                            onUpdateFunc={handleMergeAllLineItemsChange}
                            label="Van alle regels één maken"
                            disabled={isMergeLineItemsBlocked}
                        />
                        <span className="text-xs text-gray-500 ml-6">
                            Voegt alle regels samen tot één regel op basis van documenttotalen
                        </span>
                    </div>
                </div>
            </div>
        </Popup>
    );
}
